import { CRMFieldType } from '@/api/types/crm/crm.types'
import { FC } from 'react'
import iclasses from '../../styles/input.module.css'
import { InputTextarea } from '@UI/InputNew'
import cn from 'classnames'
import { Field } from '@/api/types/crm/crm.types'
import dayjs from 'dayjs'
import Select from '../base/Select/Select'
import PhoneIntegrationButtons from '../PhoneIntegrationButtons/PhoneIntegrationButtons'
import { isLeadMainPhoneField } from '@pages/Admin/CRM/utils/leadFields'
import classes from './index.module.css'
import Input from '../base/Input/Input'
import DatePicker from '../base/DatePicker/DatePicker'
import { isValidLeadLinkValue } from '@pages/Admin/CRM/utils/leadFields'
import { normalizeCrmFieldSelectOptions } from '@pages/Admin/CRM/utils/crmFieldSelectOptions'

const CRM_TEXTAREA_MAX_LENGTH = 10000
const CRM_TEXTAREA_MAX_AUTO_HEIGHT = 260

interface FieldProps {
  id: string
  name: string
  size?: 'small' | 'middle'
  field?: Field
  value?: string
  onChange: (
    e:
      | React.ChangeEvent<
          HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
      | string,
  ) => void
  error?: string
  placeholder?: string
}

export interface VariableLeadFieldSettings {
  phoneNumber?: {
    leadId: number
    fieldId: number
    fieldSettings: unknown
    withIntegrations?:
      | {
          lead: { id: number }
          fieldId: number
          fieldSettings: unknown
        }
      | false
    showVkChatButton?: boolean
    showMaxChatButton?: boolean
    showTelegramChatButton?: boolean
    showWazzupChatButton?: boolean
    vkLeadSource?: unknown
    onOpenVkChat: (options?: { conversationId?: number }) => void
    onOpenMaxChat?: (options?: { conversationId?: number }) => void
    onOpenTelegramChat?: (options?: { conversationId?: number }) => void
    onOpenWazzupChat?: (options?: { conversationId?: number }) => void
    vkUnreadCount?: number
    maxUnreadCount?: number
    telegramUnreadCount?: number
    wazzupUnreadCount?: number
  }
}

interface VariableLeadFieldProps {
  type: CRMFieldType
  fieldProps: FieldProps
  fieldSettings?: VariableLeadFieldSettings
  readOnly?: boolean
  textareaCollapsed?: boolean
}

const VariableLeadField: FC<VariableLeadFieldProps> = ({
  type,
  fieldProps: { id, name, size, field, value, onChange, error, placeholder },
  fieldSettings,
  readOnly = false,
  textareaCollapsed = false,
}) => {
  let commonProps = {
    id: id ?? field?.unique_name,
    name: name ?? field?.unique_name,
    value,
    onChange,
    error,
    placeholder: placeholder ?? field?.name,
  }

  switch (type) {
    case 'textfield':
      return (
        <>
          <Input
            size={size ?? 'small'}
            status={commonProps.error ? 'error' : undefined}
            {...commonProps}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'number':
      return (
        <>
          <Input
            size={size ?? 'small'}
            type="number"
            status={commonProps.error ? 'error' : undefined}
            {...commonProps}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'textarea':
      return (
        // @ts-ignore
        <InputTextarea
          className={cn(
            iclasses.newTextareaInputDesign,
            iclasses.newTextareaInputDesignBorderSmall,
          )}
          validate={!!error}
          errorText={error}
          typeValid="custom"
          maxLength={CRM_TEXTAREA_MAX_LENGTH}
          collapsed={textareaCollapsed}
          collapsedHeight={44}
          maxAutoHeight={CRM_TEXTAREA_MAX_AUTO_HEIGHT}
          {...commonProps}
        />
      )
    case 'select':
      return (
        <>
          <Select
            showSearch
            allowClear
            showOptionColors
            size={size ?? 'small'}
            status={error ? 'error' : undefined}
            options={normalizeCrmFieldSelectOptions(field?.value_options).map(
              (option) => ({
                label: option.label,
                value: option.label,
                title: option.label,
                color: option.color,
              }),
            )}
            {...commonProps}
            value={commonProps.value?.length ? commonProps.value : undefined}
            onClear={() => commonProps.onChange('')}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'datetime':
      return (
        <>
          <DatePicker
            showTime
            size={size ?? 'small'}
            onChange={(_, value) => commonProps.onChange(value)}
            name={commonProps.name}
            id={commonProps.id}
            status={error ? 'error' : undefined}
            placeholder={commonProps.placeholder}
            value={commonProps.value ? dayjs(commonProps.value) : null}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'date':
      return (
        <>
          <DatePicker
            // format={'YYYY.MM.DD'}
            size={size ?? 'small'}
            onChange={(_, value) => commonProps.onChange(value)}
            name={commonProps.name}
            id={commonProps.id}
            status={error ? 'error' : undefined}
            placeholder={commonProps.placeholder}
            value={commonProps.value ? dayjs(commonProps.value) : null}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    case 'phone_number': {
      const phoneSettings = fieldSettings?.phoneNumber

      return (
        <>
          <Input
            size={size ?? 'small'}
            type="tel"
            inputMode="tel"
            maxLength={30}
            status={commonProps.error ? 'error' : undefined}
            suffix={
              phoneSettings ? (
                <PhoneIntegrationButtons
                  leadId={phoneSettings.leadId}
                  fieldId={phoneSettings.fieldId}
                  fieldSettings={phoneSettings.fieldSettings}
                  showVkChatButton={
                    phoneSettings.showVkChatButton ??
                    isLeadMainPhoneField(field?.unique_name ?? '')
                  }
                  showMaxChatButton={phoneSettings.showMaxChatButton}
                  showTelegramChatButton={phoneSettings.showTelegramChatButton}
                  showWazzupChatButton={phoneSettings.showWazzupChatButton}
                  vkLeadSource={phoneSettings.vkLeadSource ?? { vk: undefined }}
                  onOpenVkChat={phoneSettings.onOpenVkChat}
                  onOpenMaxChat={phoneSettings.onOpenMaxChat}
                  onOpenTelegramChat={phoneSettings.onOpenTelegramChat}
                  onOpenWazzupChat={phoneSettings.onOpenWazzupChat}
                  vkUnreadCount={phoneSettings.vkUnreadCount}
                  maxUnreadCount={phoneSettings.maxUnreadCount}
                  telegramUnreadCount={phoneSettings.telegramUnreadCount}
                  wazzupUnreadCount={phoneSettings.wazzupUnreadCount}
                  vkUnreadBadgeSize="card"
                  showWazzupIntegrations={Boolean(phoneSettings.withIntegrations)}
                />
              ) : null
            }
            className={cn({
              [classes.inputPhoneNumberWithIntegration]: Boolean(phoneSettings),
            })}
            {...commonProps}
            onChange={(e) => {
              const raw = e.target.value
              const withPlus = raw.startsWith('+')
              const digits = raw.replace(/\D/g, '')
              // Разрешаем «+» только в начале (международный формат).
              commonProps.onChange(withPlus ? `+${digits}` : digits)
            }}
            placeholder={placeholder ?? 'Введите телефон'}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    }

    case 'link': {
      const trimmedValue = (value ?? '').trim()

      if (readOnly) {
        if (!trimmedValue) {
          return <span className={classes.linkReadOnlyEmpty}>—</span>
        }

        if (!isValidLeadLinkValue(trimmedValue)) {
          return <span className={classes.linkReadOnlyInvalid}>{trimmedValue}</span>
        }

        return (
          <a
            href={trimmedValue}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.linkReadOnly}
          >
            {trimmedValue}
          </a>
        )
      }

      return (
        <>
          <Input
            size={size ?? 'small'}
            type="url"
            inputMode="url"
            status={commonProps.error ? 'error' : undefined}
            {...commonProps}
            placeholder={placeholder ?? commonProps.placeholder ?? 'https://'}
          />
          {error && <p className={iclasses.customErrorText}>{error}</p>}
        </>
      )
    }

    default:
      return null
  }
}

export default VariableLeadField
